<template>
  <b-field label="Órgão" expanded>
    <div class="filtro-orgao-autocomplete">
      <b-autocomplete
        ref="autocomplete"
        v-model="search"
        clearable
        expanded
        field="nome"
        open-on-focus
        check-infinite-scroll
        class="filtro-orgao-autocomplete__control"
        :class="{ 'filtro-orgao-autocomplete__control--todos': showTodos }"
        :placeholder="showTodos ? '' : 'Pesquisar órgão'"
        :data="options"
        :loading="loading"
        @typing="onTyping"
        @select="select"
        @clear="clear"
        @focus="onFocus"
        @blur="onBlur"
        @infinite-scroll="onInfiniteScroll"
      >
        <template #empty>
          <span v-if="loading">Buscando...</span>
          <span v-else-if="search.trim().length < minQueryLength"
            >Digite ao menos {{ minQueryLength }} caracteres</span
          >
          <span v-else>Nenhum órgão encontrado</span>
        </template>
        <template #default="props">
          <div class="media">
            <div class="media-content">{{ optionName(props.option) }}</div>
            <div class="media-right">{{ optionUf(props.option) }}</div>
          </div>
        </template>
        <template #footer>
          <span v-if="loadingMore" class="has-text-grey is-size-7">Carregando...</span>
          <span v-else-if="!hasMore && options.length" class="has-text-grey is-size-7"
            >Todos os resultados</span
          >
        </template>
      </b-autocomplete>
      <span v-if="showTodos" class="filtro-orgao-autocomplete__todos" aria-hidden="true">
        <span class="filtro-orgao-autocomplete__tag">
          <span class="filtro-orgao-autocomplete__tag-label">Todos</span>
        </span>
      </span>
    </div>
  </b-field>
</template>

<script>
import debounce from 'lodash/debounce';
import { fetchBuyerOrgaos } from '@/resources/companies';

const PAGE_SIZE = 20;

export default {
  name: 'FiltroOrgaoAutocomplete',
  props: {
    value: { default: null },
    showTodosTag: { type: Boolean, default: true },
  },
  data() {
    return {
      search: '',
      options: [],
      selected: null,
      loading: false,
      loadingMore: false,
      hasMore: false,
      focused: false,
      minQueryLength: 4,
      pageSize: PAGE_SIZE,
      requestId: 0,
      activeQuery: '',
      blurTimer: null,
    };
  },
  computed: {
    showTodos() {
      return (
        this.showTodosTag &&
        !this.focused &&
        !this.loading &&
        (this.value == null || this.value === '') &&
        !String(this.search || '').trim()
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.syncSelectedFromValue(value);
      },
    },
  },
  created() {
    this.searchOrgaos = debounce(this.searchOrgaosImmediate, 900);
  },
  beforeDestroy() {
    if (this.searchOrgaos && this.searchOrgaos.cancel) {
      this.searchOrgaos.cancel();
    }
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
    }
  },
  methods: {
    optionName(option) {
      return (option && (option.nome || option.socialDenomination)) || '';
    },
    optionUf(option) {
      return (option && (option.uf || option.state)) || '';
    },
    namesEqual(a, b) {
      return (
        String(a || '')
          .trim()
          .toLocaleLowerCase('pt-BR')
          .localeCompare(
            String(b || '')
              .trim()
              .toLocaleLowerCase('pt-BR'),
            'pt-BR',
          ) === 0
      );
    },
    onFocus() {
      this.focused = true;
      if (this.blurTimer) {
        clearTimeout(this.blurTimer);
        this.blurTimer = null;
      }
    },
    onBlur() {
      // Atrasa o blur para não limpar a lista ao rolar o dropdown (mantém o cursor).
      this.blurTimer = setTimeout(() => {
        this.focused = false;
        this.blurTimer = null;
        if (this.selected) {
          this.search = this.optionName(this.selected);
        } else if (this.value == null || this.value === '') {
          this.search = '';
          this.options = [];
          this.hasMore = false;
          this.activeQuery = '';
        }
      }, 200);
    },
    async syncSelectedFromValue(value) {
      if (value == null || value === '') {
        this.selected = null;
        this.search = '';
        this.options = [];
        this.hasMore = false;
        this.activeQuery = '';
        return;
      }

      if (this.selected && String(this.selected.id) === String(value)) {
        this.search = this.optionName(this.selected);
        return;
      }

      this.loading = true;
      try {
        const response = await fetchBuyerOrgaos({ id: value });
        const [orgao] = response.data || [];
        this.selected = orgao || null;
        this.search = orgao ? this.optionName(orgao) : '';
        this.options = orgao ? [orgao] : [];
        this.hasMore = false;
        this.activeQuery = '';
      } catch (error) {
        this.selected = null;
        this.search = '';
        this.options = [];
        this.hasMore = false;
        this.activeQuery = '';
      } finally {
        this.loading = false;
      }
    },
    onTyping(value) {
      if (this.selected && this.namesEqual(this.optionName(this.selected), value)) {
        return;
      }

      if (this.selected) {
        this.selected = null;
        this.$emit('input', null);
      }

      this.searchOrgaos(value);
    },
    async searchOrgaosImmediate(term) {
      const query = String(term || '').trim();
      if (query.length < this.minQueryLength) {
        this.options = [];
        this.hasMore = false;
        this.activeQuery = '';
        this.loading = false;
        this.loadingMore = false;
        return;
      }

      const currentRequestId = this.requestId + 1;
      this.requestId = currentRequestId;
      this.activeQuery = query;
      this.loading = true;
      this.loadingMore = false;

      try {
        const response = await fetchBuyerOrgaos({
          q: query,
          limit: this.pageSize,
          offset: 0,
        });
        if (currentRequestId !== this.requestId) return;
        const page = Array.isArray(response.data) ? response.data : [];
        this.options = page;
        this.hasMore = page.length >= this.pageSize;
        this.keepInputFocus();
      } catch (error) {
        if (currentRequestId !== this.requestId) return;
        this.options = [];
        this.hasMore = false;
      } finally {
        if (currentRequestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    async onInfiniteScroll() {
      if (!this.hasMore || this.loading || this.loadingMore) return;
      if (!this.activeQuery || this.activeQuery.length < this.minQueryLength) return;

      const currentRequestId = this.requestId;
      const offset = this.options.length;
      this.loadingMore = true;

      try {
        const response = await fetchBuyerOrgaos({
          q: this.activeQuery,
          limit: this.pageSize,
          offset,
        });
        if (currentRequestId !== this.requestId) return;
        const page = Array.isArray(response.data) ? response.data : [];
        if (!page.length) {
          this.hasMore = false;
          return;
        }
        const knownIds = new Set(this.options.map(item => String(item.id)));
        const appended = page.filter(item => !knownIds.has(String(item.id)));
        this.options = [...this.options, ...appended];
        this.hasMore = page.length >= this.pageSize;
        this.keepInputFocus();
      } catch (error) {
        if (currentRequestId !== this.requestId) return;
        this.hasMore = false;
      } finally {
        if (currentRequestId === this.requestId) {
          this.loadingMore = false;
        }
      }
    },
    keepInputFocus() {
      this.$nextTick(() => {
        const { autocomplete } = this.$refs;
        const input =
          autocomplete &&
          (autocomplete.$refs.input
            ? autocomplete.$refs.input.$refs.input || autocomplete.$refs.input
            : null);
        if (input && typeof input.focus === 'function') {
          input.focus();
        }
      });
    },
    select(option) {
      if (this.blurTimer) {
        clearTimeout(this.blurTimer);
        this.blurTimer = null;
      }
      if (!option) {
        this.clear();
        return;
      }
      this.selected = option;
      this.search = this.optionName(option);
      this.options = [option];
      this.hasMore = false;
      this.activeQuery = '';
      this.$emit('input', option.id);
    },
    clear() {
      if (this.blurTimer) {
        clearTimeout(this.blurTimer);
        this.blurTimer = null;
      }
      this.search = '';
      this.selected = null;
      this.options = [];
      this.hasMore = false;
      this.activeQuery = '';
      this.$emit('input', null);
    },
  },
};
</script>

<style lang="scss" scoped>
.filtro-orgao-autocomplete {
  position: relative;
  width: 100%;
}

.filtro-orgao-autocomplete__todos {
  align-items: center;
  bottom: 0;
  display: flex;
  left: 0.75rem;
  pointer-events: none;
  position: absolute;
  right: 2.5rem;
  top: 0;
  z-index: 2;
}

.filtro-orgao-autocomplete__tag {
  align-items: center;
  background: #f5f5f5;
  border-radius: 2px;
  color: #363636;
  display: inline-flex;
  font-size: 0.75rem;
  max-width: 100%;
  padding: 0.15rem 0.5rem;
}

.filtro-orgao-autocomplete__tag-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filtro-orgao-autocomplete__control--todos ::v-deep .input {
  caret-color: #363636;
  color: transparent;
}

// Mantém a cor do ícone no foco; escurece só no hover do próprio ícone.
.filtro-orgao-autocomplete__control ::v-deep .control.has-icons-right {
  .input:focus ~ .icon,
  .input.is-focused ~ .icon,
  .input:active ~ .icon {
    color: #dbdbdb;
  }

  .icon.is-clickable:hover {
    color: #7a7a7a;
  }
}
</style>
